// ───────────────────────────────────────────────────────────────────────────
// Demo core: deterministic randomness + relative dates
// Every generator takes its own rngFor('<namespace>') stream, so adding data in
// one namespace never reshuffles another. Dates are computed relative to
// `new Date()` at call time, in UTC, so the demo always looks "current".
// ───────────────────────────────────────────────────────────────────────────

export interface Rng {
  /** Float in [0, 1). */
  next(): number;
  /** Integer in [min, max] (both inclusive). */
  int(min: number, max: number): number;
  /** Float in [min, max), rounded to `decimals` (default 2). */
  float(min: number, max: number, decimals?: number): number;
  pick<T>(arr: readonly T[]): T;
  /** True with probability p (0..1). */
  chance(p: number): boolean;
  /** Fisher–Yates shuffle into a NEW array (input untouched). */
  shuffle<T>(arr: readonly T[]): T[];
}

/** mulberry32: tiny, fast, good-enough 32-bit seeded PRNG. Returns floats in [0, 1). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-1a 32-bit hash of a string (unsigned). */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function round(value: number, decimals = 2): number {
  const f = 10 ** decimals;
  return Math.round(value * f) / f;
}

export function makeRng(next: () => number): Rng {
  const rng: Rng = {
    next,
    int(min, max) {
      const lo = Math.ceil(Math.min(min, max));
      const hi = Math.floor(Math.max(min, max));
      return lo + Math.floor(next() * (hi - lo + 1));
    },
    float(min, max, decimals = 2) {
      return round(min + next() * (max - min), decimals);
    },
    pick<T>(arr: readonly T[]): T {
      if (arr.length === 0) throw new Error('[demo] rng.pick on empty array');
      return arr[Math.floor(next() * arr.length)];
    },
    chance(p) {
      return next() < p;
    },
    shuffle<T>(arr: readonly T[]): T[] {
      const out = arr.slice();
      for (let i = out.length - 1; i > 0; i -= 1) {
        const j = Math.floor(next() * (i + 1));
        const tmp = out[i];
        out[i] = out[j];
        out[j] = tmp;
      }
      return out;
    },
  };
  return rng;
}

/** A fresh deterministic stream for `namespace`: same namespace -> same sequence. */
export function rngFor(namespace: string): Rng {
  return makeRng(mulberry32(hashString(namespace)));
}

// ── Dates (UTC) ────────────────────────────────────────────────────────────

export const DAY_MS = 24 * 60 * 60 * 1000;
export const HOUR_MS = 60 * 60 * 1000;

/** 00:00:00.000 UTC today. */
export function startOfToday(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
}

/** 00:00 UTC, n days before today. */
export function daysAgo(n: number): Date {
  return new Date(startOfToday().getTime() - n * DAY_MS);
}

/** 00:00 UTC, n days after today. */
export function daysFromNow(n: number): Date {
  return new Date(startOfToday().getTime() + n * DAY_MS);
}

/** Exactly n hours before now (not truncated). */
export function hoursAgo(n: number): Date {
  return new Date(Date.now() - n * HOUR_MS);
}

/** 1st of the month 00:00 UTC; offset 0 = this month, -1 = last month, +1 = next. */
export function monthStart(offset = 0): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
}

/** 'YYYY-MM' (UTC). */
export function monthKey(d: Date): string {
  return d.toISOString().slice(0, 7);
}

/** Monday 00:00 UTC of this week (+ weekOffset weeks; -1 = last week). */
export function mondayOf(weekOffset = 0): Date {
  const today = startOfToday();
  const sinceMonday = (today.getUTCDay() + 6) % 7;
  return new Date(today.getTime() - sinceMonday * DAY_MS + weekOffset * 7 * DAY_MS);
}

/** Full ISO string with Z, e.g. '2026-10-25T09:30:00.000Z'. */
export function iso(d: Date): string {
  return d.toISOString();
}

/** 'YYYY-MM-DD' (UTC). */
export function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Naive ISO without zone, e.g. '2026-10-25T09:30:00' (the shape naive backend datetimes use). */
export function naiveIso(d: Date): string {
  return d.toISOString().slice(0, 19);
}

/** Day of month count for the month containing `d` (UTC). */
export function daysInMonth(d: Date): number {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
}

/**
 * Smooth yearly curve: base ± amplitude, peaking in January (month 0) and
 * bottoming out in July (month 6) — the Saudi summer slowdown. Pass a negative
 * amplitude for summer-peaking series (electricity / cooling).
 */
export function seasonal(monthIndex0to11: number, base: number, amplitude: number): number {
  return base + amplitude * Math.cos((2 * Math.PI * monthIndex0to11) / 12);
}
